<script setup>
import { ref, getCurrentInstance } from 'vue';
import { router } from '@inertiajs/vue3';
import { useToast } from 'primevue/usetoast';

const props = defineProps({
    visible: Boolean,
    contentId: [Number, String],
});

const emit = defineEmits(['update:visible', 'submitted']);

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();

const rejectionReason = ref('');
const processing = ref(false);

const close = () => {
    rejectionReason.value = '';
    emit('update:visible', false);
};

const submit = () => {
    if (!rejectionReason.value.trim()) return;
    processing.value = true;
    router.post(
        proxy.route('client.order-content.client-reject', props.contentId),
        { rejection_reason: rejectionReason.value },
        {
            onSuccess: () => {
                toast.add({ severity: 'success', summary: $t('Success'), detail: $t('Content rejected'), life: 3000 });
                close();
                emit('submitted');
            },
            onError: (errors) => {
                const msg = errors?.error || errors?.message || $t('Failed to reject content');
                toast.add({ severity: 'error', summary: $t('Error'), detail: msg, life: 5000 });
            },
            onFinish: () => { processing.value = false; }
        }
    );
};
</script>

<template>
    <Dialog
        :visible="visible"
        @update:visible="(v) => emit('update:visible', v)"
        :header="$t('Reject Content')"
        modal
        :closable="!processing"
        :style="{ width: '500px' }"
        :breakpoints="{ '640px': '95vw' }"
    >
        <div class="space-y-4">
            <div class="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div class="flex items-start gap-3">
                    <i class="pi pi-exclamation-triangle text-red-600 text-lg mt-0.5"></i>
                    <div>
                        <p class="font-medium text-red-800 mb-1">{{ $t('Reason for rejection') }}</p>
                        <p class="text-sm text-red-600">{{ $t('Please explain why you are rejecting this content so the team can improve it.') }}</p>
                    </div>
                </div>
            </div>
            <Textarea
                v-model="rejectionReason"
                :placeholder="$t('Describe the issues with the article...')"
                rows="4"
                class="w-full"
                :disabled="processing"
            />
        </div>

        <template #footer>
            <div class="flex justify-end gap-2">
                <Button
                    :label="$t('Cancel')"
                    icon="pi pi-times"
                    text
                    :disabled="processing"
                    @click="close"
                />
                <Button
                    :label="$t('Submit Rejection')"
                    icon="pi pi-send"
                    severity="danger"
                    :loading="processing"
                    :disabled="processing || !rejectionReason.trim()"
                    @click="submit"
                />
            </div>
        </template>
    </Dialog>
</template>
